<template>
    <section class="products-section">
        <div class="container">

            <!-- Search info -->
            <div v-if="filters.search" class="catalog-search-info">
                <span>Results for</span>
                <strong>"{{ filters.search }}"</strong>
                <a :href="baseURL + '/products'" class="clear">✕ Clear</a>
            </div>

            <!-- Toolbar -->
            <div class="catalog-toolbar">
                <div class="catalog-filters">
                    <a v-if="filters.tag" :href="baseURL + '/products'" class="filter-tag active">
                        {{ filters.tag }} <span class="remove">✕</span>
                    </a>
                    <a v-if="filters.collection" :href="baseURL + '/products'" class="filter-tag active">
                        {{ collectionName || filters.collection }} <span class="remove">✕</span>
                    </a>
                </div>

                <div class="catalog-sort">
                    <label for="sortSelect">Sort:</label>
                    <select id="sortSelect" v-model="filters.sort" @change="resetAndLoad">
                        <option value="newest">Newest</option>
                        <option value="price_asc">Price: Low → High</option>
                        <option value="price_desc">Price: High → Low</option>
                        <option value="name_asc">Name: A → Z</option>
                    </select>
                </div>
            </div>

            <!-- Count -->
            <div v-if="total" class="catalog-count">
                {{ total }} product{{ total !== 1 ? 's' : '' }}
            </div>

            <!-- Grid -->
            <div v-if="items.length" class="product-grid">

                <a :href="baseURL + '/product/' + product.slug"
                    v-for="product in items"
                    :key="product.id"
                    class="product-card"
                >
                <div
                    class="product-card-image"
                    :style="{ background: product.category_gradient || '' }"
                >
                    <img v-if="product.image_thumb" :src="product.image_thumb" :alt="product.name" loading="lazy">
                    <img v-else-if="product.image_path" :src="product.image_path" :alt="product.name" loading="lazy">
                    <div v-else class="emoji-placeholder">{{ product.emoji || '📦' }}</div>

                    <span v-if="product.price > 0 && product.sale_price < product.price" class="product-card-badge">
                        -{{ Math.round( ( 1 - product.sale_price / product.price ) * 100 ) }}%
                    </span>
                </div>

                <div class="product-card-body">
                    <div class="product-card-name">{{ product.name }}</div>

                    <div class="product-card-footer">
                        <div class="product-card-price">
                            <span class="current">{{ formatMoney( product.sale_price ) }}</span>
                            <span v-if="product.price > 0 && product.sale_price < product.price" class="original">
                                {{ formatMoney( product.price ) }}
                            </span>
                        </div>

                        <div v-if="product.rating_count > 0" class="product-card-rating">
                            <span class="star">★</span>
                            {{ product.rating_avg }}
                        </div>
                    </div>
                </div>
                </a>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="cat-products-loading">
                <span></span><span></span><span></span>
            </div>

            <!-- Empty state -->
            <div v-if="! loading && ! items.length" class="catalog-empty">
                <div class="emoji">🔍</div>
                <h3>No products found</h3>
                <p>Try adjusting your filters or search terms</p>
            </div>

            <!-- Sentinel -->
            <div ref="sentinel" class="cat-products-sentinel"></div>

        </div>
    </section>
</template>

<script>
import { trackEcommerce, gtmItem, GTM_EVENTS } from '../../js/utils/gtm';

export default {
    name: 'ProductsList',

    data() {

        const params = new URLSearchParams( window.location.search );

        return {
            items    : [],
            page     : 1,
            total    : 0,
            loading  : false,
            hasMore  : true,
            observer : null,
            baseURL  : window.baseURL || '',
            filters  : {
                search     : params.get('search')     || '',
                tag        : params.get('tag')        || '',
                collection : params.get('collection') || '',
                sort       : params.get('sort')       || 'newest',
            },

            collectionName : '',
        };
    },

    mounted() {
        this.loadProducts();
        this.initObserver();
    },

    beforeUnmount() {
        if( this.observer ) this.observer.disconnect();
    },

    methods: {

        async loadProducts() {

            if( this.loading || ! this.hasMore )
                return;

            this.loading = true;

            try {
                const response = await this.$http.get( '/products/list', {
                    params: {
                        ...this.filters,
                        page : this.page,
                    },
                });

                const { status, data } = response?.data || {};

                if( status === 'success' ) {
                    const offset   = this.items.length;          // позиция первого нового item
                    const newItems = data.items || [];

                    this.items   = [ ...this.items, ...newItems ];
                    this.total   = data.total || 0;
                    this.collectionName = data.collection_name || this.filters.collection;
                    this.hasMore = this.items.length < this.total;
                    this.page++;

                    this.trackList( newItems, offset );
                }
            } catch( e ) {
                console.error( 'Failed to load products:', e );
            } finally {
                this.loading = false;
            }
        },

        // GA4 view_item_list - для каждой подгруженной пачки (с учётом позиции).
        trackList( products, offset ) {

            if( ! products.length )
                return;

            const listId = this.filters.collection || this.filters.tag
                || ( this.filters.search ? 'search' : 'catalog' );

            trackEcommerce( GTM_EVENTS.VIEW_ITEM_LIST, {
                currency       : 'USD',
                item_list_id   : listId,
                item_list_name : this.collectionName || listId,
                items          : products.map( ( p, i ) => gtmItem( {
                    id       : p.slug,
                    name     : p.name,
                    category : 'product',
                    price    : +( ( p.sale_price || p.price || 0 ) / 100 ).toFixed( 2 ),
                    index    : offset + i,
                } ) ),
            } );
        },

        resetAndLoad() {
            this.items   = [];
            this.page    = 1;
            this.hasMore = true;
            this.total   = 0;
            this.loadProducts();
        },

        initObserver() {

            this.observer = new IntersectionObserver(
                ( entries ) => {
                    if( entries[0].isIntersecting && ! this.loading && this.hasMore )
                        this.loadProducts();
                },
                { rootMargin: '200px' },
            );

            this.$nextTick(() => {
                if( this.$refs.sentinel )
                    this.observer.observe( this.$refs.sentinel );
            });
        },

        formatMoney( cents ) {

            if( ! cents )
                return '';

            return new Intl.NumberFormat( 'en-US', {
                style    : 'currency',
                currency : 'USD',
            }).format( cents / 100 );
        },
    },
};
</script>